// PRD 05 §3.2 — one target column (환자용 or 보호자용) inside the editor.
// Holds the column header (target badge + count + 추가 / 복사하여 추가 buttons)
// and the body (question cards or empty state).

import { useTranslation } from 'react-i18next';
import { ClipboardList, Copy, Plus } from 'lucide-react';

import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

import { QuestionCard } from './QuestionCard';
import type { QuestionRenderNode } from '../utils/question-layout';
import type { Applicability, Question, Target } from '../types/questionnaire-design.types';

interface QuestionColumnProps {
  target: Target;
  nodes: QuestionRenderNode[];
  jumpDisplayNumByQid: Map<string, string>;
  // SHCT-582 — PRD 21 §7.4 P-2: 추가 / 복사하여 추가 and every card action stay on screen without
  // PRO_MGMT:WRITE and are `disabled` + 툴팁 instead of being withheld.
  canWrite: boolean;
  // SHCT-517 / [ADM.046] — the item's Scope, which every card in this column inherits and displays.
  // Threaded from the panel (which already resolves the item off the tree) rather than looked up per
  // card, so all cards in a column can only ever show one answer.
  itemScope: Applicability | null;
  onAdd?: () => void;
  // Copy stays noop until S5 ships the modal.
  onCopy?: () => void;
  // Per-card hooks. Passed by qid so the column doesn't need to know the
  // shape of the panel state.
  onEdit?: (qid: string) => void;
  onAddSub?: (parentQid: string) => void;
  // SHCT-862 — opens the copy modal against this main question as the parent.
  onCopyAsSub?: (parentQid: string) => void;
  onPreview?: (qid: string) => void;
  onDelete?: (qid: string) => void;
  // SHCT-239 BR-01 — when a disease filter is active, cards that don't match
  // are dimmed (not removed, so a parent stays visible for a matching child),
  // mirroring the tree's dim behavior.
  isDimmed?: (question: Question) => boolean;
}

const DIMMED_OPACITY = 0.35;

const TARGET_LABEL_KEY: Record<Target, string> = {
  PATIENT: 'enums.target.patient',
  CAREGIVER: 'enums.target.caregiver',
};

const BADGE_CLASS: Record<Target, string> = {
  PATIENT: 'badge-primary',
  CAREGIVER: 'badge-warning',
};

const HEADER_BORDER: Record<Target, string> = {
  PATIENT: 'var(--info)',
  CAREGIVER: 'var(--badge-warning-text)',
};

export function QuestionColumn({
  target,
  nodes,
  jumpDisplayNumByQid,
  canWrite,
  itemScope,
  onAdd,
  onCopy,
  onEdit,
  onAddSub,
  onCopyAsSub,
  onPreview,
  onDelete,
  isDimmed,
}: QuestionColumnProps) {
  const { t } = useTranslation('questionnaire-design');
  const { denyProps, tooltip } = useWriteDenyGuard(canWrite);
  const label = t(TARGET_LABEL_KEY[target]);
  // Card-row count = top-level questions, matching the prototype's "N문항"
  // header label.
  const count = nodes.length;

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          borderBottom: `2px solid ${HEADER_BORDER[target]}`,
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span
            className={`badge ${BADGE_CLASS[target]}`}
            style={{ fontSize: 10, padding: '2px 8px' }}
          >
            {label}
          </span>
          <span style={{ whiteSpace: 'nowrap', fontSize: 'var(--font-size-xs)' }}>
            {t('column.count', { count })}
          </span>
        </span>
        <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {onAdd ? (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={onAdd}
              style={{ fontSize: 11 }}
              data-perm="PRO_MGMT:WRITE"
              disabled={!canWrite}
              {...denyProps}
            >
              <Plus size={12} /> {t('column.addButton')}
            </button>
          ) : null}
          {onCopy ? (
            <button
              type="button"
              className="btn btn-sm btn-default btn-secondary"
              onClick={onCopy}
              style={{ fontSize: 11 }}
              data-perm="PRO_MGMT:WRITE"
              disabled={!canWrite}
              {...denyProps}
              title={tooltip ?? t('column.copyTooltip', { target: label })}
            >
              <Copy size={12} /> {t('column.copyButton')}
            </button>
          ) : null}
        </span>
      </div>

      <div style={{ padding: 12 }}>
        {count === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 12px',
              color: 'var(--muted-foreground)',
            }}
          >
            <ClipboardList size={32} style={{ display: 'block', margin: '0 auto 8px' }} />
            <p style={{ fontSize: 'var(--font-size-xs)' }}>
              {t('column.empty', { target: label })}
            </p>
            {onAdd ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={onAdd}
                style={{ marginTop: 8, fontSize: 11 }}
                data-perm="PRO_MGMT:WRITE"
                disabled={!canWrite}
                {...denyProps}
              >
                <Plus size={12} /> {t('column.addButton')}
              </button>
            ) : null}
          </div>
        ) : (
          nodes.map((node) => (
            <div key={node.question.qid}>
              <div style={{ opacity: isDimmed?.(node.question) ? DIMMED_OPACITY : 1 }}>
                <QuestionCard
                  question={node.question}
                  displayNum={node.displayNum}
                  isSub={false}
                  canWrite={canWrite}
                  itemScope={itemScope}
                  jumpDisplayNumByQid={jumpDisplayNumByQid}
                  onEdit={onEdit ? () => onEdit(node.question.qid) : undefined}
                  onAddSub={onAddSub ? () => onAddSub(node.question.qid) : undefined}
                  onCopyAsSub={
                    onCopyAsSub ? () => onCopyAsSub(node.question.qid) : undefined
                  }
                  onPreview={onPreview ? () => onPreview(node.question.qid) : undefined}
                  onDelete={onDelete ? () => onDelete(node.question.qid) : undefined}
                />
              </div>
              {node.children.map((sub) => (
                <div
                  key={sub.question.qid}
                  style={{ opacity: isDimmed?.(sub.question) ? DIMMED_OPACITY : 1 }}
                >
                  <QuestionCard
                    question={sub.question}
                    displayNum={sub.displayNum}
                    isSub
                    jumpDisplayNumByQid={jumpDisplayNumByQid}
                    canWrite={canWrite}
                  itemScope={itemScope}
                    onEdit={onEdit ? () => onEdit(sub.question.qid) : undefined}
                    onPreview={onPreview ? () => onPreview(sub.question.qid) : undefined}
                    onDelete={onDelete ? () => onDelete(sub.question.qid) : undefined}
                  />
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
